import React, { useState, useEffect, useCallback } from 'react';
import { Select, Modal, Table, Input, Button, Form } from 'antd';
import { ArrowLeft, Edit } from 'lucide-react';
import Swal from 'sweetalert2';
import moment from 'moment';
import {
  getTeamAppraisals,
  reviewAppraisal,
  searchTeamAppraisals,
} from '../../service/appraisalService';

const ReviewTeam = () => {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [appraisalName, setAppraisalName] = useState('');
  const [periodName, setPeriodName] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [status, setStatus] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [form] = Form.useForm();

  const getUserId = () => {
    try {
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'User data not found. Please log in again.',
        });
        return null;
      }
      const user = JSON.parse(storedUserData);
      return user.data._id;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to retrieve user data.',
      });
      return null;
    }
  };

  const userId = getUserId();

  const fetchAppraisals = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTeamAppraisals(userId);
      setAppraisals(data || []);
      setPagination((prev) => ({ ...prev, total: data ? data.length : 0 }));
    } catch (error) {
      setAppraisals([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchAppraisals();
    }
  }, [userId, fetchAppraisals]);

  const handleSearch = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User not logged in.',
      });
      return;
    }
    setLoading(true);
    try {
      const records = await searchTeamAppraisals({
        userId,
        appraisalName,
        periodName,
        employeeName,
        status,
        setLoading,
      });
      setAppraisals(records || []);
      setPagination((prev) => ({ ...prev, total: records ? records.length : 0, current: 1 }));
    } catch (error) {
      setAppraisals([]);
      setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleReviewAppraisal = async (values) => {
    try {
      const behavioralScores = {
        teamwork: Number(values.teamwork),
        communication: Number(values.communication),
        punctuality: Number(values.punctuality),
      };
      const updatedAppraisal = await reviewAppraisal(userId, selectedAppraisal._id, {
        behavioral_scores: behavioralScores,
        comments: values.comments,
      });
      setAppraisals((prev) =>
        prev.map((appraisal) =>
          appraisal._id === updatedAppraisal._id ? updatedAppraisal : appraisal
        )
      );
      setOpenReviewModal(false);
      form.resetFields();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Appraisal reviewed successfully.',
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to review appraisal.',
      });
    }
  };

  const columns = [
    {
      title: 'Appraisal Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Employee',
      dataIndex: 'employee_id',
      key: 'employee',
      render: (employee) => employee?.full_name || 'N/A',
    },
    {
      title: 'Period',
      dataIndex: 'period_id',
      key: 'period',
      render: (period) => period?.name || 'N/A',
    },
    {
      title: 'Department',
      dataIndex: 'department_id',
      key: 'department',
      render: (department) => department?.departmentName || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Final Score',
      dataIndex: 'final_score',
      key: 'final_score',
      render: (score) => score || 'N/A',
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <div className="action-table-data">
          <Button
            className="me-2 p-2"
            onClick={() => {
              setSelectedAppraisal(record);
              setOpenReviewModal(true);
              form.setFieldsValue({
                teamwork: record.behavioral_scores.teamwork || 0,
                communication: record.behavioral_scores.communication || 0,
                punctuality: record.behavioral_scores.punctuality || 0,
                comments: record.comments || '',
              });
            }}
            title="Review appraisal"
            icon={<Edit size={16} />}
          />
        </div>
      ),
    },
  ];

  if (!userId) {
    return (
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>Team Appraisals</h4>
                  <h6>Please log in to view team appraisals</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Team Appraisals</h4>
                <h6>Manage your team’s performance appraisals</h6>
              </div>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: '100%', marginRight: '20px' }}>
            <div
              className="container mt-3"
              style={{ maxWidth: '100%', marginLeft: '20px', marginRight: '40px' }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Appraisal Name</label>
                  <Input
                    value={appraisalName}
                    onChange={(e) => setAppraisalName(e.target.value)}
                    placeholder="Enter appraisal name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Period Name</label>
                  <Input
                    value={periodName}
                    onChange={(e) => setPeriodName(e.target.value)}
                    placeholder="Enter period name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Employee</label>
                  <Input
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Status</label>
                  <Select
                    className="select"
                    options={[
                      { value: 'Draft', label: 'Draft' },
                      { value: 'UnderReview', label: 'Under Review' },
                      { value: 'Completed', label: 'Completed' },
                    ]}
                    placeholder="Select status"
                    value={status}
                    onChange={(value) => setStatus(value)}
                    allowClear
                    showSearch
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
              </div>
            </div>
            <div
              className="container mt-0"
              style={{ maxWidth: '100%', marginLeft: '20px', marginRight: '40px' }}
            >
              <div className="row d-flex align-items-center justify-content-start">
                <div className="col-12 col-md-2 mb-3 text-start">
                  <Button
                    disabled={loading}
                    type="primary"
                    onClick={handleSearch}
                    style={{ height: '38px', width: '55%' }}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="card table-list-card-grid">
          <div className="card-body">
            <div className="table-top"></div>
            <div className="table-responsive">
              <Table
                loading={loading}
                columns={columns}
                dataSource={appraisals}
                rowKey="_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  pageSizeOptions: [5, 10, 20, 50],
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleTableChange}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        zIndex={200000}
        open={openReviewModal}
        onCancel={() => {
          setSelectedAppraisal(null);
          setOpenReviewModal(false);
          form.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={false}
        title={`Review Appraisal for ${selectedAppraisal?.employee_id?.full_name || 'Employee'}`}
      >
        <Form form={form} onFinish={handleReviewAppraisal} layout="vertical">
          <Form.Item
            label="Teamwork (1-5)"
            name="teamwork"
            rules={[
              { required: true, message: 'Please enter teamwork score' },
              {
                type: 'number',
                min: 1,
                max: 5,
                message: 'Score must be between 1 and 5',
                transform: (value) => Number(value),
              },
            ]}
          >
            <Input type="number" min={1} max={5} placeholder="Enter teamwork score" />
          </Form.Item>
          <Form.Item
            label="Communication (1-5)"
            name="communication"
            rules={[
              { required: true, message: 'Please enter communication score' },
              {
                type: 'number',
                min: 1,
                max: 5,
                message: 'Score must be between 1 and 5',
                transform: (value) => Number(value),
              },
            ]}
          >
            <Input type="number" min={1} max={5} placeholder="Enter communication score" />
          </Form.Item>
          <Form.Item
            label="Punctuality (1-5)"
            name="punctuality"
            rules={[
              { required: true, message: 'Please enter punctuality score' },
              {
                type: 'number',
                min: 1,
                max: 5,
                message: 'Score must be between 1 and 5',
                transform: (value) => Number(value),
              },
            ]}
          >
            <Input type="number" min={1} max={5} placeholder="Enter punctuality score" />
          </Form.Item>
          <Form.Item
            label="Comments"
            name="comments"
            rules={[{ required: true, message: 'Please enter comments' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter feedback and comments" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Submit Review
            </Button>
            <Button
              style={{ marginLeft: '10px' }}
              onClick={() => {
                setSelectedAppraisal(null);
                setOpenReviewModal(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReviewTeam;